/**
 * Help: live chat with Aurix support (D79). Polls every 3 s while open (D75) and marks replies as
 * read. Recovery phrases and private keys are refused before sending (and by the server).
 */
import { ChatComposer, ChatImage, ChatThread, Card, NeverShareNotice, Notice, Skeleton } from "@custos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { PageHeader } from "../components/AppShell";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { qk } from "../lib/queries";

export function HelpPage() {
  const queryClient = useQueryClient();
  const chat = useQuery({ queryKey: qk.chat, queryFn: () => api.chat(), refetchInterval: 3_000 });
  const unread = chat.data?.unread ?? 0;

  useEffect(() => {
    if (unread > 0) {
      void api.markChatRead().then(() => {
        queryClient.setQueryData(qk.chatUnread, 0);
      });
    }
  }, [unread, queryClient]);

  const closed = chat.data?.conversation?.status === "CLOSED";

  return (
    <>
      <PageHeader title="Help" subtitle="Chat with Aurix support. We reply as soon as we can." />
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <NeverShareNotice />
        <Card className="flex flex-col gap-4">
          {chat.isPending ? (
            <Skeleton className="h-40" />
          ) : chat.error ? (
            <Notice tone="danger">{errorMessage(chat.error)}</Notice>
          ) : (
            <ChatThread
              me="USER"
              messages={chat.data.messages}
              emptyText="Ask us anything about your account, deposits, withdrawals or the vault."
              renderImage={(id) => <ChatImage load={() => api.chatImage(id)} />}
              className="max-h-[55dvh] min-h-48 overflow-y-auto pr-1"
            />
          )}
          {closed && (
            <p className="text-center text-xs text-muted">
              Your last conversation is closed — a new message starts a new one.
            </p>
          )}
          <ChatComposer
            onSend={async (input) => {
              try {
                await api.sendChat(input);
              } catch (e) {
                throw new Error(errorMessage(e), { cause: e });
              }
              await queryClient.invalidateQueries({ queryKey: qk.chat });
            }}
          />
        </Card>
      </div>
    </>
  );
}
